// @ts-check
// Guarda das telas do módulo Atendimento (3.0.0, contrato 10 de atendimento, seção 24.1): toda página do atendimento,
// dos bots e da retenção é montada dentro dela. Com o módulo desligado na escola, "Este módulo não está ativo na sua
// escola"; sem a flag da tela, "Sem acesso". As rotas REST conferem de novo no servidor: a guarda só decide o que aparece.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { hasCurrentPermissionFlag, isTenantModuleEnabled } from '../../data/metadata-store.js';
import { api } from '../../data/api-client.js';
import { SERVICE_SCREEN_PATHS, serviceScreenAccess } from '../../lib/navigation-visibility.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Button } from '../../ui/button.js';
import { t } from '../../i18n/index.js';
import { MODULE_LABEL_KEYS } from '../../../../shared/modules/tenant-modules.js';

/** @typedef {'home' | 'inbox' | 'consultations' | 'ombudsman' | 'retention' | 'bots' | 'settings'} ServiceScreenKey */

/** Ícone do cabeçalho de cada tela (os mesmos dos itens do menu da seção SERVICE). */
export const SERVICE_SCREEN_ICONS = Object.freeze(/** @type {Record<ServiceScreenKey, string>} */ ({
  home: 'IconHeadset', inbox: 'IconInbox', consultations: 'IconMessageQuestion', ombudsman: 'IconSpeakerphone',
  retention: 'IconHeartHandshake', bots: 'IconRobot', settings: 'IconSettings',
}));

/** @param {{ screenKey: ServiceScreenKey, children?: any }} props */
function ServiceScreenFrame({ screenKey, children }) {
  return html`<${PageCard}
    className="service-screen"
    header=${html`<${PageHeader} icon=${SERVICE_SCREEN_ICONS[screenKey] ?? 'IconHeadset'} title=${t(`serviceDesk.screens.${screenKey}`)} />`}
  >
    <div class="page-centered campaigns-state service-screen-state">${children}</div>
  <//>`;
}

/** @param {{ screenKey: ServiceScreenKey }} props */
export function ServiceModuleDisabled({ screenKey }) {
  return html`<${ServiceScreenFrame} screenKey=${screenKey}>
    <div data-testid="service-module-disabled">
      <${EmptyState}
        icon="IconLock"
        title=${t('tenantModules.moduleDisabledTitle')}
        subtitle=${t('tenantModules.moduleDisabledDescription', { module: t(MODULE_LABEL_KEYS.SERVICE_DESK) })}
        action=${html`<${Button} variant="secondary" title=${t('tenantModules.moduleDisabledBack')} onClick=${() => navigate('/')} />`}
      />
    </div>
  <//>`;
}

/** @param {{ screenKey: ServiceScreenKey }} props */
export function ServiceNoAccess({ screenKey }) {
  return html`<${ServiceScreenFrame} screenKey=${screenKey}>
    <div data-testid="service-no-access">
      <${EmptyState}
        icon="IconForbid"
        title=${t('serviceDesk.noAccess.title')}
        subtitle=${t(`serviceDesk.noAccess.description.${screenKey}`)}
        action=${html`<${Button} variant="secondary" title=${t('serviceDesk.noAccess.back')} onClick=${() => navigate('/')} />`}
      />
    </div>
  <//>`;
}

/** @param {{ screenKey: ServiceScreenKey, children?: any }} props */
export function ServiceScreenGuard({ screenKey, children }) {
  const access = serviceScreenAccess({ screenKey, hasPermissionFlag: hasCurrentPermissionFlag, isModuleEnabled: isTenantModuleEnabled });
  if (access === 'MODULE_DISABLED') return html`<${ServiceModuleDisabled} screenKey=${screenKey} />`;
  if (access === 'NO_ACCESS') return html`<${ServiceNoAccess} screenKey=${screenKey} />`;
  return children ?? null;
}

/**
 * Para onde /service leva (seção 24.1): a Caixa; o professor que não está em nenhum departamento vai para as Consultas.
 * @param {{ isTeacher?: boolean, teams?: unknown[] } | null | undefined} me resposta de GET /rest/service/me
 */
export function serviceHomePath(me) {
  if (me?.isTeacher && Array.isArray(me.teams) && me.teams.length === 0) return SERVICE_SCREEN_PATHS.consultations;
  return SERVICE_SCREEN_PATHS.inbox;
}

/** /service: pergunta quem atende e redireciona; sem resposta, a Caixa. */
export function ServiceHomeRedirect() {
  const [isResolving, setIsResolving] = useState(true);
  useEffect(() => {
    let isCancelled = false;
    api.get('/rest/service/me')
      .then((response) => serviceHomePath(response?.data))
      .catch(() => SERVICE_SCREEN_PATHS.inbox)
      .then((path) => {
        if (isCancelled) return;
        setIsResolving(false);
        navigate(path, { replace: true });
      });
    return () => {
      isCancelled = true;
    };
  }, []);
  return isResolving ? html`<${ServiceScreenFrame} screenKey="home"><${Skeleton} width=${240} height=${16} /><//>` : null;
}
